/**
 * Central TanStack Query key factory. Every feature uses these so invalidation is
 * predictable across the app (e.g. after a recipe edit, invalidate `recipes.all`).
 */
export const queryKeys = {
  config: ['config'] as const,
  me: ['me'] as const,
  profile: ['profile'] as const,
  sessions: ['sessions'] as const,
  recipes: {
    all: ['recipes'] as const,
    list: (params: Record<string, unknown>) => ['recipes', 'list', params] as const,
    detail: (id: string) => ['recipes', 'detail', id] as const,
    scale: (id: string, servings: string, convert: boolean | string) =>
      ['recipes', 'scale', id, servings, convert] as const,
    revisions: (id: string) => ['recipes', 'revisions', id] as const,
    variations: (id: string) => ['recipes', 'variations', id] as const,
    share: (id: string) => ['recipes', 'share', id] as const,
    rating: (id: string) => ['recipes', 'rating', id] as const,
    cookingLog: (id: string) => ['recipes', 'cooking-log', id] as const,
    public: (slug: string) => ['recipes', 'public', slug] as const,
  },
  cookingLog: ['cooking-log'] as const,
  taxonomy: { categories: ['categories'] as const, tags: ['tags'] as const },
  units: ['units'] as const,
  search: {
    suggest: (q: string) => ['search', 'suggest', q] as const,
    facets: (params: Record<string, unknown>) => ['search', 'facets', params] as const,
  },
  media: { asset: (id: string) => ['media', id] as const },
  collections: {
    all: ['collections'] as const,
    detail: (id: string) => ['collections', id] as const,
  },
  imports: { all: ['imports'] as const, job: (id: string) => ['imports', id] as const },
  ai: {
    generation: (id: string) => ['ai', 'generation', id] as const,
    generations: ['ai', 'generations'] as const,
    conversations: ['ai', 'conversations'] as const,
    conversation: (id: string) => ['ai', 'conversation', id] as const,
  },
  aiChat: {
    status: ['ai-chat', 'status'] as const,
    conversations: (q: string) => ['ai-chat', 'conversations', q] as const,
    conversation: (id: string) => ['ai-chat', 'conversation', id] as const,
  },
  timers: ['timers'] as const,
  households: {
    all: ['households'] as const,
    detail: (id: string) => ['households', id] as const,
    invitation: (token: string) => ['households', 'invitation', token] as const,
  },
  mealPlan: (from: string, to: string, householdId?: string) =>
    ['meal-plan', from, to, householdId ?? null] as const,
  shopping: { all: ['shopping'] as const, detail: (id: string) => ['shopping', id] as const },
  receipts: {
    all: ['receipts'] as const,
    detail: (id: string) => ['receipts', id] as const,
  },
  prices: { inbox: ['prices', 'inbox'] as const },
  pantry: {
    all: ['pantry'] as const,
    match: (params: Record<string, unknown>) => ['pantry', 'match', params] as const,
  },
  tasting: {
    all: ['tasting'] as const,
    visitsAll: ['tasting', 'visits'] as const,
    visits: (params: Record<string, unknown>) => ['tasting', 'visits', 'list', params] as const,
    visit: (id: string) => ['tasting', 'visits', 'detail', id] as const,
    map: (params: Record<string, unknown>) => ['tasting', 'visits', 'map', params] as const,
    placesAll: ['tasting', 'places'] as const,
    places: (params: Record<string, unknown>) => ['tasting', 'places', 'list', params] as const,
    place: (id: string) => ['tasting', 'places', 'detail', id] as const,
    /** OpenStreetMap lookups (client-side); keyed by ~10 m so GPS jitter does not refetch. */
    nearby: (position: { lat: number; lng: number } | null) =>
      [
        'tasting',
        'osm',
        'nearby',
        position ? `${position.lat.toFixed(4)},${position.lng.toFixed(4)}` : null,
      ] as const,
    placeSearch: (q: string, near: { lat: number; lng: number } | null) =>
      [
        'tasting',
        'osm',
        'search',
        q.toLowerCase(),
        near ? `${near.lat.toFixed(2)},${near.lng.toFixed(2)}` : null,
      ] as const,
    origins: (recipeId: string) => ['tasting', 'origins', recipeId] as const,
  },
  catalog: {
    all: ['catalog'] as const,
    ingredients: (params: Record<string, unknown>) => ['catalog', 'ingredients', params] as const,
    ingredient: (id: string) => ['catalog', 'ingredient', id] as const,
    unlinked: ['catalog', 'unlinked'] as const,
    products: (params: Record<string, unknown>) => ['catalog', 'products', params] as const,
    product: (id: string) => ['catalog', 'product', id] as const,
    listHints: (listId: string, storeId: string | null) =>
      ['catalog', 'list-hints', listId, storeId] as const,
    recipeNutrition: (recipeId: string) => ['catalog', 'recipe-nutrition', recipeId] as const,
    recipeLinks: (recipeId: string) => ['catalog', 'recipe-links', recipeId] as const,
  },
  notifications: ['notifications'] as const,
  exports: ['exports'] as const,
  admin: {
    users: (params: Record<string, unknown>) => ['admin', 'users', params] as const,
    user: (id: string) => ['admin', 'user', id] as const,
    health: ['admin', 'health'] as const,
    jobs: (params: Record<string, unknown>) => ['admin', 'jobs', params] as const,
    flags: ['admin', 'flags'] as const,
    aiProviders: ['admin', 'ai-providers'] as const,
    nutritionProviders: ['admin', 'nutrition-providers'] as const,
    catalogBackfill: ['admin', 'catalog-backfill'] as const,
    entitlements: (userId: string) => ['admin', 'entitlements', userId] as const,
    units: ['admin', 'units'] as const,
    densities: ['admin', 'densities'] as const,
    scraperFailures: (params: Record<string, unknown>) =>
      ['admin', 'scraper', 'failures', params] as const,
    scraperStats: ['admin', 'scraper', 'stats'] as const,
    domainRules: ['admin', 'scraper', 'domain-rules'] as const,
    abuseReports: (params: Record<string, unknown>) => ['admin', 'abuse-reports', params] as const,
    auditLog: (params: Record<string, unknown>) => ['admin', 'audit-log', params] as const,
    supportGrants: ['admin', 'support-grants'] as const,
    mailStatus: ['admin', 'mail', 'status'] as const,
    mailSettings: ['admin', 'mail', 'settings'] as const,
    mailTemplates: ['admin', 'mail', 'templates'] as const,
    mailTemplate: (key: string, locale: string) =>
      ['admin', 'mail', 'template', key, locale] as const,
    mailTemplateVersions: (key: string, locale: string) =>
      ['admin', 'mail', 'template-versions', key, locale] as const,
    mailMessages: (params: Record<string, unknown>) =>
      ['admin', 'mail', 'messages', params] as const,
    mailSuppressions: ['admin', 'mail', 'suppressions'] as const,
    mailWebhookEvents: (params: Record<string, unknown>) =>
      ['admin', 'mail', 'webhook-events', params] as const,
    supportConversations: (params: Record<string, unknown>) =>
      ['admin', 'support', 'conversations', params] as const,
    supportConversation: (id: string) => ['admin', 'support', 'conversation', id] as const,
    supportUnread: ['admin', 'support', 'unread'] as const,
    supportStaff: ['admin', 'support', 'staff'] as const,
    supportSettings: ['admin', 'support', 'settings'] as const,
  },
} as const;
